import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Radar, ArrowRight, Shield, Sparkles, LayoutGrid, Compass, Rocket, PawPrint, Target, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ensureAnonUser } from "@/lib/anonSession";
import { toast } from "sonner";
import axios from "axios";
import InstallPrompt from "@/components/hola/InstallPrompt";
import WelcomeCarousel from "@/components/hola/WelcomeCarousel";
import AdsPreview from "@/components/hola/AdsPreview";
import BackButton from "@/components/hola/BackButton";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ScanPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const demoSessionId = params.get("demo");
  const [loading, setLoading] = useState(false);
  const [showShortcutModal, setShowShortcutModal] = useState(false);
  const [showModePicker, setShowModePicker] = useState(false);
  const [demoInfo, setDemoInfo] = useState(null); // {session_id, area_name, session_minutes}
  const [demoBooting, setDemoBooting] = useState(false);

  // Si el QR trae ?demo=<id> arrancamos el flujo de PRUEBA
  useEffect(() => {
    if (!demoSessionId) return;
    (async () => {
      setDemoBooting(true);
      try {
        const { data } = await axios.post(`${API}/api/pwa/demo/scan`, { demo_session: demoSessionId });
        // El backend devuelve un device_id específico de demo — lo guardamos
        localStorage.setItem("holaradar_device_id", data.device_id);
        localStorage.setItem("holaradar_demo_session", data.session_id);
        localStorage.setItem("holaradar_demo_expires", data.expires_at);
        setDemoInfo(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No pude iniciar la prueba");
      } finally {
        setDemoBooting(false);
      }
    })();
  }, [demoSessionId]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      await ensureAnonUser();
      toast.success("Radar activado. Elige cómo quieres explorar.");
      setShowModePicker(true);
    } catch (e) {
      toast.error("No se pudo activar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const startDemo = () => {
    // La demo va directo a la guía (Aura habla con el prospecto)
    localStorage.setItem("hr_scan_mode", "guide");
    navigate("/guide");
  };

  const pickMode = (mode) => {
    localStorage.setItem("hr_scan_mode", mode);
    if (mode === "directory") navigate("/radar");
    else if (mode === "manada") navigate("/manada");
    else if (mode === "juego") navigate("/juego");
    else if (mode === "cuponera") navigate("/cuponera");
    else if (mode === "mi") navigate("/mi?nuevo=1");
    else navigate("/guide");
  };

  useEffect(() => {
    // Muestra el popup de "acceso directo" una sola vez al entrar por primera vez desde el QR.
    if (typeof window === "undefined") return;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    const alreadyShown = localStorage.getItem("hr_shortcut_modal_shown") === "1";
    if (!standalone && !alreadyShown) {
      // pequeño delay para que la landing "entre" antes que el modal
      const t = setTimeout(() => setShowShortcutModal(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const closeModal = () => {
    localStorage.setItem("hr_shortcut_modal_shown", "1");
    setShowShortcutModal(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF0F7] to-white">
      {/* Toma-over para modo prueba (asesor) */}
      {demoSessionId && (
        <div className="max-w-md mx-auto px-6 pt-8 pb-10">
          <div className="rounded-2xl bg-gradient-to-br from-[#FF007A] to-[#0055FF] p-6 text-white shadow-2xl" data-testid="demo-prospect-hero">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur grid place-items-center">
                <Rocket className="w-4 h-4" />
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Modo Prueba</div>
            </div>
            {demoBooting ? (
              <div className="text-sm">Preparando tu prueba…</div>
            ) : demoInfo ? (
              <>
                <h1 className="font-heading font-extrabold text-3xl leading-tight">
                  ¡Hola! Vas a probar HOLA RADAR
                </h1>
                <p className="text-sm opacity-90 mt-2 leading-relaxed">
                  Verás {demoInfo.area_name && <strong>{demoInfo.area_name}</strong>} — un mall simulado con 5 comercios cerca de ti.
                  Aura, nuestra guía virtual, te presentará cada uno como lo haría con tus clientes reales.
                </p>
                <div className="mt-3 text-xs bg-white/15 rounded-lg px-3 py-2 inline-block">
                  ⏱️ Duración: <strong>{demoInfo.session_minutes} min</strong>
                </div>
                <Button
                  onClick={startDemo}
                  className="w-full mt-5 h-12 rounded-full bg-white text-[#FF007A] hover:bg-neutral-100 font-bold text-base"
                  data-testid="demo-start-guide-btn"
                >
                  <Sparkles className="w-4 h-4" /> Empezar prueba con Aura
                </Button>
              </>
            ) : (
              <div className="text-sm">La sesión de prueba no es válida o ya expiró.</div>
            )}
          </div>
        </div>
      )}
      {!demoSessionId && (
      <div className="max-w-md mx-auto px-6 pt-6 pb-10">
        <div className="mb-2 -ml-2">
          <BackButton to="/" label="Regresar" testid="scan-back-btn" />
        </div>
        <div className="text-center mt-6">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-[#FF007A] shadow-xl shadow-[#FF007A]/40 mb-6"
          >
            <QrCode className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">
            Bienvenido a <span className="text-[#FF007A]">HOLA RADAR</span>
          </h1>
          <p className="mt-4 text-neutral-600 leading-relaxed">
            Escaneaste el QR. Al tocar el botón activaremos tu radar de ofertas GPS.
            <strong className="text-black"> Sin registro, sin datos personales.</strong>
          </p>
        </div>

        {/* Bienvenida emotiva rotante */}
        <div className="mt-8">
          <WelcomeCarousel />
        </div>

        {/* Muestra de cómo se verán las promociones */}
        <div className="mt-10">
          <AdsPreview />
        </div>

        <div className="mt-10 space-y-4">
          <FeatureRow icon={Radar} title="Radar activo a 50m" desc="Recibes ofertas de comercios que estás pasando." />
          <FeatureRow icon={Shield} title="Anónimo" desc="Solo guardamos un identificador aleatorio en tu celular." />
          <FeatureRow icon={Sparkles} title="Chat con IA" desc="Explora ofertas conversando con el asistente del local." />
        </div>

        <Button
          onClick={handleActivate}
          disabled={loading}
          data-testid="activate-radar-btn"
          className="mt-10 h-14 w-full rounded-full bg-[#FF007A] hover:bg-[#e5006e] text-white font-bold text-base shadow-lg shadow-[#FF007A]/30"
        >
          {loading ? "Activando..." : (<>Activar mi Radar <ArrowRight className="w-5 h-5" /></>)}
        </Button>

        <p className="text-xs text-center text-neutral-500 mt-4">
          Al continuar aceptas compartir tu ubicación GPS mientras el radar esté encendido. Puedes apagarlo en cualquier momento.
        </p>

        <div className="mt-8">
          <InstallPrompt variant="banner" />
        </div>
      </div>
      )}
      {showShortcutModal && <InstallPrompt variant="modal" onDismiss={closeModal} />}
      {showModePicker && (
        <ModePicker onPick={pickMode} onClose={() => setShowModePicker(false)} />
      )}
    </div>
  );
}

function ModePicker({ onPick, onClose }) {
  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="mode-picker">
      <div className="min-h-full flex items-center justify-center px-5 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-br from-[#FF007A] to-[#0055FF] text-white text-center px-6 pt-8 pb-6">
            <h2 className="font-heading font-extrabold text-2xl leading-tight" data-testid="mode-picker-title">
              ¿Cómo prefieres recibir<br />las promociones y regalos?
            </h2>
            <p className="text-sm opacity-90 mt-2">Elige tu experiencia. Puedes cambiar cuando quieras.</p>
          </div>
          <div className="p-5 space-y-4">
            <ModeCard
              testid="mode-directory"
              icon={LayoutGrid}
              badge="Clásico"
              title="Directorio de ofertas"
              desc="Recibes todas las promociones cercanas a la vez, en un carrusel para deslizar y elegir lo que te llame la atención."
              color="[#0055FF]"
              onClick={() => onPick("directory")}
            />
            <ModeCard
              testid="mode-guide"
              icon={Compass}
              badge="Nuevo"
              title="Guía virtual"
              desc='Una guía IA (como un tour de museo) te va presentando los comercios afiliados uno por uno conforme caminas. Ella te cuenta qué venden y sus ofertas.'
              color="[#FF007A]"
              onClick={() => onPick("guide")}
            />
            <ModeCard
              testid="mode-manada"
              icon={PawPrint}
              badge="MANADA"
              title="Descuentos en grupo"
              desc="¿Vas en manada? Entre más amigos vayan contigo, más ahorras. Genera un código y aplícalo al pagar. Perfecto para restaurantes, salones y más."
              color="[#0055FF]"
              onClick={() => onPick("manada")}
            />
            <ModeCard
              testid="mode-juego"
              icon={Target}
              badge="JUEGO"
              title="Atínale al Precio"
              desc="Adivina el precio exacto de una prenda o servicio de comercios afiliados. ¿Le atinas? Te lo llevas GRATIS. Refuerza tu suerte y conoce marcas locales."
              color="[#FF007A]"
              onClick={() => onPick("juego")}
            />
            <ModeCard
              testid="mode-mi"
              icon={Sparkles}
              badge="MI CUENTA"
              title="Mi cuenta HOLA RADAR"
              desc="Tu espacio personal: cupones, boletos del sorteo mensual, chat con Aura y los comercios, y el radar. Todo en un solo lugar, sin registro."
              color="[#10b981]"
              onClick={() => onPick("mi")}
            />
            <ModeCard
              testid="mode-cuponera"
              icon={Ticket}
              badge="CUPONERA"
              title="Mis cupones de descuento"
              desc="Guarda los cupones que te regalan los comercios al escanear su QR personalizado. Ve cuánto ahorras y canjéalos en caja."
              color="[#0055FF]"
              onClick={() => onPick("cuponera")}
            />
            <button
              onClick={onClose}
              className="w-full text-center text-xs text-neutral-500 mt-2 hover:text-neutral-800"
              data-testid="mode-picker-close"
            >
              Ahora no, decido después
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function ModeCard({ icon: Icon, badge, title, desc, color, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-2xl border-2 border-neutral-200 hover:border-${color} hover:bg-${color}/5 transition-all p-4 flex gap-3 group`}
    >
      <div className={`w-12 h-12 rounded-2xl bg-${color}/10 text-${color} grid place-items-center shrink-0 group-hover:scale-110 transition-transform`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-widest text-${color}`}>{badge}</span>
        </div>
        <div className="font-heading font-extrabold text-lg leading-tight">{title}</div>
        <p className="text-sm text-neutral-600 mt-1 leading-snug">{desc}</p>
      </div>
      <ArrowRight className={`w-5 h-5 text-neutral-300 group-hover:text-${color} shrink-0 self-center transition-colors`} />
    </button>
  );
}

function FeatureRow({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3 p-4 rounded-2xl bg-white border border-neutral-200">
      <div className="w-10 h-10 rounded-xl bg-[#FF007A]/10 text-[#FF007A] grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-heading font-bold">{title}</div>
        <div className="text-sm text-neutral-600">{desc}</div>
      </div>
    </div>
  );
}
